import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  Alert,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  Text,
  Button,
  IconButton,
  useTheme,
  FAB,
  Searchbar,
  Chip,
  Modal,
  Portal,
} from 'react-native-paper';
import {
  Users,
  Search,
  Filter,
  Settings,
  TrendingUp,
  MessageCircle,
  RefreshCw,
  Sparkles,
  ArrowLeft,
} from 'lucide-react-native';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchPeerMatches,
  generatePeerMatches,
  sendConnectionRequest,
  respondToConnectionRequest,
  updateMatchingPreferences,
  fetchMatchingAnalytics,
  selectPeerMatches,
  selectConnectionRequests,
  selectMatchingPreferences,
  selectMatchingAnalytics,
  selectPeerMatchingLoading,
  selectPeerMatchingError,
  selectMatchingInProgress,
  selectHighCompatibilityMatches,
  selectPendingConnections,
  updateMatchStatus,
  type PeerMatch,
  type PeerMatchingPreferences,
} from '../../store/slices/peerMatchingSlice';
import {
  PeerMatchingCard,
  PeerProfileDisplay,
  ConnectionRequestSystem,
  PeerMatchingAnalytics,
} from '../../components/ai';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { logger } from '../../utils/logger';

type TabType = 'discover' | 'connections' | 'requests' | 'analytics';

interface FilterModalProps {
  visible: boolean;
  preferences: PeerMatchingPreferences | null;
  onDismiss: () => void;
  onSave: (preferences: Partial<PeerMatchingPreferences>) => void;
}

function FilterModal({
  visible,
  preferences,
  onDismiss,
  onSave,
}: FilterModalProps) {
  const theme = useTheme();
  const [localPreferences, setLocalPreferences] = useState<
    Partial<PeerMatchingPreferences>
  >({});

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleSave = () => {
    onSave(localPreferences);
    onDismiss();
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[
          styles.modalContainer,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <View style={styles.modalHeader}>
          <Text
            variant='titleMedium'
            style={[styles.modalTitle, { color: theme.colors.onSurface }]}
          >
            Matching Preferences
          </Text>
          <IconButton icon='close' size={20} onPress={onDismiss} />
        </View>

        <View style={styles.modalContent}>
          <Text
            variant='bodyMedium'
            style={[styles.preferenceLabel, { color: theme.colors.onSurface }]}
          >
            Skill Level Range
          </Text>
          <View style={styles.skillRangeContainer}>
            <Chip
              mode={
                localPreferences.skillLevelRange?.[0] === 0
                  ? 'flat'
                  : 'outlined'
              }
              onPress={() =>
                setLocalPreferences(prev => ({
                  ...prev,
                  skillLevelRange: [0, prev.skillLevelRange?.[1] || 1],
                }))
              }
            >
              Beginner
            </Chip>
            <Chip
              mode={
                localPreferences.skillLevelRange?.[0] === 0.3
                  ? 'flat'
                  : 'outlined'
              }
              onPress={() =>
                setLocalPreferences(prev => ({
                  ...prev,
                  skillLevelRange: [0.3, prev.skillLevelRange?.[1] || 1],
                }))
              }
            >
              Intermediate
            </Chip>
            <Chip
              mode={
                localPreferences.skillLevelRange?.[0] === 0.7
                  ? 'flat'
                  : 'outlined'
              }
              onPress={() =>
                setLocalPreferences(prev => ({
                  ...prev,
                  skillLevelRange: [0.7, 1],
                }))
              }
            >
              Advanced
            </Chip>
          </View>

          <Text
            variant='bodyMedium'
            style={[styles.preferenceLabel, { color: theme.colors.onSurface }]}
          >
            Group Size Preference
          </Text>
          <View style={styles.groupSizeContainer}>
            {(['one_on_one', 'small_group', 'large_group', 'any'] as const).map(
              size => (
                <Chip
                  key={size}
                  mode={
                    localPreferences.groupSizePreference === size
                      ? 'flat'
                      : 'outlined'
                  }
                  onPress={() =>
                    setLocalPreferences(prev => ({
                      ...prev,
                      groupSizePreference: size,
                    }))
                  }
                  style={styles.groupSizeChip}
                >
                  {size
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase())}
                </Chip>
              )
            )}
          </View>

          <Text
            variant='bodyMedium'
            style={[styles.preferenceLabel, { color: theme.colors.onSurface }]}
          >
            Communication Style
          </Text>
          <View style={styles.communicationContainer}>
            {(['formal', 'casual', 'mixed'] as const).map(style => (
              <Chip
                key={style}
                mode={
                  localPreferences.communicationStyle === style
                    ? 'flat'
                    : 'outlined'
                }
                onPress={() =>
                  setLocalPreferences(prev => ({
                    ...prev,
                    communicationStyle: style,
                  }))
                }
                style={styles.communicationChip}
              >
                {style.charAt(0).toUpperCase() + style.slice(1)}
              </Chip>
            ))}
          </View>
        </View>

        <View style={styles.modalActions}>
          <Button
            mode='outlined'
            onPress={onDismiss}
            style={styles.modalActionButton}
          >
            Cancel
          </Button>
          <Button
            mode='contained'
            onPress={handleSave}
            style={[styles.modalActionButton, styles.saveButton]}
            labelStyle={styles.saveButtonLabel}
          >
            Save Preferences
          </Button>
        </View>
      </Modal>
    </Portal>
  );
}

export function PeerMatchingScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  // Redux state
  const matches = useAppSelector(selectPeerMatches);
  const connectionRequests = useAppSelector(selectConnectionRequests);
  const preferences = useAppSelector(selectMatchingPreferences);
  const analytics = useAppSelector(selectMatchingAnalytics);
  const loading = useAppSelector(selectPeerMatchingLoading);
  const error = useAppSelector(selectPeerMatchingError);
  const matchingInProgress = useAppSelector(selectMatchingInProgress);
  const highCompatibilityMatches = useAppSelector(
    selectHighCompatibilityMatches
  );
  const pendingConnections = useAppSelector(selectPendingConnections);

  // Local state
  const [activeTab, setActiveTab] = useState<TabType>('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<PeerMatch | null>(null);

  // Mock user ID - in real app, get from auth state
  const userId = 'current-user-id';

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      await Promise.all([
        dispatch(fetchPeerMatches(userId)).unwrap(),
        dispatch(fetchMatchingAnalytics(userId)).unwrap(),
      ]);
    } catch (error) {
      logger.error('Failed to load peer matching data:', error);
    }
  }, [dispatch, userId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } finally {
      setRefreshing(false);
    }
  }, [loadInitialData]);

  const handleGenerateNewMatches = useCallback(async () => {
    try {
      await dispatch(generatePeerMatches(userId)).unwrap();
      Alert.alert(
        'Success',
        'New matches generated based on your preferences!'
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to generate new matches. Please try again.');
    }
  }, [dispatch, userId]);

  const handleConnect = useCallback(
    async (matchId: string, message?: string) => {
      const match = matches.find(m => m.id === matchId);
      if (!match) return;

      try {
        await dispatch(
          sendConnectionRequest({
            toUserId: match.peer.userId,
            message,
            useAIIntro: !message, // Use AI intro if no custom message
          })
        ).unwrap();

        dispatch(updateMatchStatus({ matchId, status: 'pending' }));
        Alert.alert('Success', 'Connection request sent!');
      } catch (error) {
        Alert.alert(
          'Error',
          'Failed to send connection request. Please try again.'
        );
      }
    },
    [dispatch, matches]
  );

  const handleDismissMatch = useCallback(
    (matchId: string, reason?: string) => {
      dispatch(updateMatchStatus({ matchId, status: 'declined' }));
      // TODO: Send dismissal feedback to improve future matches
    },
    [dispatch]
  );

  const handleViewProfile = useCallback((userId: string) => {
    // TODO: Navigate to peer profile screen
    logger.log('View profile:', userId);
  }, []);

  const handleShowCompatibilityDetails = useCallback((compatibility: any) => {
    // TODO: Show detailed compatibility modal
    logger.log('Show compatibility details:', compatibility);
  }, []);

  const handleRespondToRequest = useCallback(
    async (
      requestId: string,
      action: 'accept' | 'decline',
      message?: string
    ) => {
      try {
        await dispatch(
          respondToConnectionRequest({
            requestId,
            action,
            message,
          })
        ).unwrap();

        const successMessage =
          action === 'accept'
            ? 'Connection request accepted!'
            : 'Connection request declined.';
        Alert.alert('Success', successMessage);
      } catch (error) {
        Alert.alert(
          'Error',
          'Failed to respond to connection request. Please try again.'
        );
      }
    },
    [dispatch]
  );

  const handleCancelRequest = useCallback((requestId: string) => {
    // TODO: Implement cancel request functionality
    logger.log('Cancel request:', requestId);
  }, []);

  const handleUpdatePreferences = useCallback(
    async (newPreferences: Partial<PeerMatchingPreferences>) => {
      try {
        await dispatch(updateMatchingPreferences(newPreferences)).unwrap();
        Alert.alert('Success', 'Matching preferences updated!');
      } catch (error) {
        Alert.alert('Error', 'Failed to update preferences. Please try again.');
      }
    },
    [dispatch]
  );

  const filteredMatches = matches.filter(match => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      match.peer.displayName.toLowerCase().includes(query) ||
      match.peer.username.toLowerCase().includes(query) ||
      match.peer.masteryAreas.some(area => area.toLowerCase().includes(query))
    );
  });

  const renderDiscoverTab = () => (
    <View style={styles.tabContent}>
      {/* Search and Filters */}
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder='Search peers by name or interests...'
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchBar}
          inputStyle={styles.searchInput}
          icon={Search}
        />
        <IconButton
          icon={Filter}
          size={24}
          onPress={() => setShowFilters(true)}
          style={styles.filterButton}
        />
      </View>

      {/* High Compatibility Matches */}
      {highCompatibilityMatches.length > 0 && (
        <View style={styles.highCompatibilitySection}>
          <Text
            variant='titleMedium'
            style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
          >
            🌟 Excellent Matches
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.sectionSubtitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            High compatibility peers perfect for collaboration
          </Text>
        </View>
      )}

      {/* Matches List */}
      <FlatList
        data={filteredMatches}
        renderItem={({ item }) => (
          <PeerMatchingCard
            match={item}
            onConnect={handleConnect}
            onDismiss={handleDismissMatch}
            onViewProfile={handleViewProfile}
            onShowCompatibilityDetails={handleShowCompatibilityDetails}
            showAnalytics={true}
          />
        )}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.matchesList}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Users size={64} color={theme.colors.onSurfaceVariant} />
            <Text
              variant='headlineSmall'
              style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
            >
              No matches found
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtext,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              {searchQuery
                ? 'Try adjusting your search or filters'
                : 'Generate new matches based on your learning profile'}
            </Text>
            {!searchQuery && (
              <Button
                mode='contained'
                onPress={handleGenerateNewMatches}
                style={styles.generateButton}
                labelStyle={styles.generateButtonLabel}
                icon={Sparkles}
                loading={matchingInProgress}
                disabled={matchingInProgress}
              >
                Generate Matches
              </Button>
            )}
          </View>
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );

  const renderConnectionsTab = () => (
    <View style={styles.tabContent}>
      {/* Connected Peers */}
      <Text
        variant='titleMedium'
        style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
      >
        Your Connections
      </Text>
      <Text
        variant='bodySmall'
        style={[
          styles.sectionSubtitle,
          { color: theme.colors.onSurfaceVariant },
        ]}
      >
        Peers you're connected with for learning together
      </Text>

      {/* TODO: Implement connected peers list */}
      <View style={styles.emptyContainer}>
        <MessageCircle size={64} color={theme.colors.onSurfaceVariant} />
        <Text
          variant='headlineSmall'
          style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
        >
          No connections yet
        </Text>
        <Text
          variant='bodyMedium'
          style={[
            styles.emptySubtext,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          Start connecting with peers to build your learning network
        </Text>
      </View>
    </View>
  );

  const renderRequestsTab = () => (
    <ConnectionRequestSystem
      sentRequests={connectionRequests.sent}
      receivedRequests={connectionRequests.received}
      onRespondToRequest={handleRespondToRequest}
      onCancelRequest={handleCancelRequest}
      onSendRequest={() => {}} // Not used in this context
      loading={loading.connections}
    />
  );

  const renderAnalyticsTab = () => (
    <View style={styles.tabContent}>
      {analytics ? (
        <PeerMatchingAnalytics
          analytics={analytics}
          showDetailedBreakdown={true}
        />
      ) : (
        <View style={styles.emptyContainer}>
          <TrendingUp size={64} color={theme.colors.onSurfaceVariant} />
          <Text
            variant='headlineSmall'
            style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
          >
            No analytics data
          </Text>
          <Text
            variant='bodyMedium'
            style={[
              styles.emptySubtext,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Start connecting with peers to see your matching analytics
          </Text>
        </View>
      )}
    </View>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'discover':
        return renderDiscoverTab();
      case 'connections':
        return renderConnectionsTab();
      case 'requests':
        return renderRequestsTab();
      case 'analytics':
        return renderAnalyticsTab();
      default:
        return renderDiscoverTab();
    }
  };

  if (loading.matches && matches.length === 0) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <LoadingSpinner />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Action Header */}
      <View style={styles.header}>
        <View style={styles.headerSpacer} />
        <Text
          variant='headlineSmall'
          style={[styles.headerTitle, { color: theme.colors.onSurface }]}
        >
          Peer Matching
        </Text>
        <IconButton
          icon={Settings}
          size={24}
          onPress={() => setShowFilters(true)}
        />
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'discover' && {
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setActiveTab('discover')}
        >
          <Text
            variant='bodyMedium'
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'discover'
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant,
                fontWeight: activeTab === 'discover' ? '600' : '400',
              },
            ]}
          >
            Discover
          </Text>
          {highCompatibilityMatches.length > 0 && activeTab === 'discover' && (
            <View
              style={[
                styles.tabBadge,
                { backgroundColor: theme.colors.primary },
              ]}
            >
              <Text style={styles.tabBadgeText}>
                {highCompatibilityMatches.length}
              </Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'connections' && {
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setActiveTab('connections')}
        >
          <Text
            variant='bodyMedium'
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'connections'
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant,
                fontWeight: activeTab === 'connections' ? '600' : '400',
              },
            ]}
          >
            Connections
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'requests' && {
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setActiveTab('requests')}
        >
          <Text
            variant='bodyMedium'
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'requests'
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant,
                fontWeight: activeTab === 'requests' ? '600' : '400',
              },
            ]}
          >
            Requests
          </Text>
          {pendingConnections.length > 0 && (
            <View style={[styles.tabBadge, { backgroundColor: '#FF6B35' }]}>
              <Text style={styles.tabBadgeText}>
                {pendingConnections.length}
              </Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'analytics' && {
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setActiveTab('analytics')}
        >
          <Text
            variant='bodyMedium'
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'analytics'
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant,
                fontWeight: activeTab === 'analytics' ? '600' : '400',
              },
            ]}
          >
            Analytics
          </Text>
        </TouchableOpacity>
      </View>

      {/* Error Display */}
      {error && <ErrorMessage message={error} onRetry={loadInitialData} />}

      {/* Tab Content */}
      {renderTabContent()}

      {/* FAB for generating new matches */}
      {activeTab === 'discover' && (
        <FAB
          icon={RefreshCw}
          label='New Matches'
          onPress={handleGenerateNewMatches}
          style={[styles.fab, { backgroundColor: theme.colors.primary }]}
          loading={matchingInProgress}
          disabled={matchingInProgress}
        />
      )}

      {/* Filter Modal */}
      <FilterModal
        visible={showFilters}
        preferences={preferences}
        onDismiss={() => setShowFilters(false)}
        onSave={handleUpdatePreferences}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerSpacer: {
    width: 24,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  tabNavigation: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  tabText: {
    fontSize: 14,
  },
  tabBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  tabBadgeText: {
    color: 'white',
    fontSize: 10,
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  searchBar: {
    flex: 1,
    elevation: 0,
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  searchInput: {
    fontSize: 14,
  },
  filterButton: {
    margin: 0,
  },
  highCompatibilitySection: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 12,
  },
  matchesList: {
    paddingHorizontal: 16,
    paddingBottom: 100, // Account for FAB
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  generateButton: {
    backgroundColor: '#4ECDC4',
    minWidth: 160,
  },
  generateButtonLabel: {
    color: 'white',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  modalContainer: {
    margin: 20,
    borderRadius: 12,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontWeight: '600',
  },
  modalContent: {
    gap: 20,
  },
  preferenceLabel: {
    fontWeight: '500',
    marginBottom: 8,
  },
  skillRangeContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  groupSizeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  groupSizeChip: {
    minWidth: 80,
  },
  communicationContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  communicationChip: {
    flex: 1,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 20,
  },
  modalActionButton: {
    flex: 1,
  },
  saveButton: {
    backgroundColor: '#4ECDC4',
  },
  saveButtonLabel: {
    color: 'white',
  },
});
